import { useEffect, useRef } from "react";
import { Box, Stack, Typography } from "@mui/material";
import TemplateFieldCard from "./TemplateFieldCard";

const FIELD_LIST_MAX_HEIGHT = 620;
const SCROLL_MARGIN_PX = 8;

/**
 * The mapping panel's field list: a fixed-height scroller of inline-editable cards. Pure
 * presentation, every piece of state stays with the page.
 */
const TemplateFieldList = ({
  fields,
  selectedIndex,
  onSelect,
  onChange,
  onFieldTypeChange,
  onPrefillKeyChange,
  onDelete,
}) => {
  const scrollerRef = useRef(null);

  // Selecting a field on the page must reveal its card, and vice versa. Scrolling the scroller
  // itself rather than calling scrollIntoView keeps the surrounding page still, so the preview
  // does not jump while the canvas is busy centring the same field's rectangle.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const card = scroller?.querySelector(`[data-field-index="${selectedIndex}"]`);
    if (!scroller || !card || typeof scroller.scrollTo !== "function") return;
    const cardBox = card.getBoundingClientRect();
    const scrollerBox = scroller.getBoundingClientRect();
    if (cardBox.top >= scrollerBox.top && cardBox.bottom <= scrollerBox.bottom) return;
    scroller.scrollTo({
      top: scroller.scrollTop + (cardBox.top - scrollerBox.top) - SCROLL_MARGIN_PX,
      behavior: "smooth",
    });
  }, [selectedIndex]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">Fields ({fields.length})</Typography>
        {selectedIndex !== null && fields[selectedIndex] && (
          <Typography variant="caption" color="text.secondary">
            Draw on the page to place {fields[selectedIndex].label || fields[selectedIndex].name}
          </Typography>
        )}
      </Stack>

      <Box ref={scrollerRef} sx={{ maxHeight: FIELD_LIST_MAX_HEIGHT, overflowY: "auto", pr: 1 }}>
        {fields.map((field, index) => (
          // Keyed by position, never by name: names may legitimately repeat so one value can be
          // mirrored onto several spots, and a shared key would make React reuse the wrong card.
          <TemplateFieldCard
            key={index}
            index={index}
            field={field}
            isSelected={index === selectedIndex}
            onSelect={() => onSelect(index)}
            onChange={(key, value) => onChange(index, key, value)}
            onFieldTypeChange={(fieldType) => onFieldTypeChange(index, fieldType)}
            onPrefillKeyChange={(prefillKey) => onPrefillKeyChange(index, prefillKey)}
            onDelete={() => onDelete(index)}
          />
        ))}
        {fields.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No fields yet. Add one, then draw its box on the page.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TemplateFieldList;
